/**
 * Behaviour shared by the application shell: keyboard awareness, scroll
 * restoration, consistent back navigation and picking up new releases.
 */
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

const PHONE = '(max-width: 767.98px) and (pointer: coarse)';
const isEditable = (el) =>
  !!el && (el.isContentEditable ||
    (el.tagName === 'TEXTAREA') ||
    (el.tagName === 'INPUT' && !['button', 'checkbox', 'radio', 'range', 'submit', 'file', 'color'].includes(el.type)));

/**
 * Marks <html data-keyboard="open"> while a text field has focus on a
 * touch phone, so the bottom bar steps aside and the field's own controls
 * (send buttons, composers) sit directly above the on-screen keyboard.
 */
export function useKeyboardState() {
  useEffect(() => {
    const root = document.documentElement;
    const mq = window.matchMedia(PHONE);
    const sync = () => {
      const open = mq.matches && isEditable(document.activeElement);
      if (open) root.setAttribute('data-keyboard', 'open');
      else root.removeAttribute('data-keyboard');
    };
    // focusout fires before focus lands elsewhere; settle on the next frame
    const later = () => requestAnimationFrame(sync);
    document.addEventListener('focusin', sync);
    document.addEventListener('focusout', later);
    mq.addEventListener?.('change', sync);
    return () => {
      document.removeEventListener('focusin', sync);
      document.removeEventListener('focusout', later);
      mq.removeEventListener?.('change', sync);
      root.removeAttribute('data-keyboard');
    };
  }, []);
}

/**
 * New pages open at the top; Back/Forward return to where the member was.
 * (The shell no longer remounts per route, so the browser cannot do this.)
 */
export function useScrollRestoration() {
  const location = useLocation();
  const navType = useNavigationType();
  const positions = useRef(new Map());
  const currentKey = useRef(location.key);

  // Record continuously: by the time a route change is committed the old
  // page is gone and the document may already be shorter.
  useEffect(() => {
    if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => positions.current.set(currentKey.current, window.scrollY));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, []);

  useLayoutEffect(() => {
    const key = location.key;
    currentKey.current = key;
    const target = navType === 'POP' ? positions.current.get(key) ?? 0 : 0;
    window.scrollTo({ top: target, left: 0, behavior: 'instant' });
    if (target <= 0) return undefined;
    // Returning pages refetch and their media resize as it loads. Re-apply
    // the position whenever the page grows, until it settles (3 s) or the
    // member scrolls, taps or types — never fight the member.
    const apply = () => window.scrollTo({ top: target, left: 0, behavior: 'instant' });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
    ro?.observe(document.body);
    const stop = () => {
      ro?.disconnect();
      clearTimeout(timer);
      ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach((t) => window.removeEventListener(t, stop, true));
    };
    const timer = setTimeout(stop, 3000);
    ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach((t) => window.addEventListener(t, stop, { capture: true, passive: true }));
    return stop;
  }, [location.key, navType]);
}

/**
 * Back that never leaves the app: history when the member navigated here,
 * otherwise (deep link, fresh PWA launch) the page's logical parent.
 */
export function useBack(fallback = '/home') {
  const navigate = useNavigate();
  return useCallback(() => {
    const idx = window.history.state?.idx;
    if (typeof idx === 'number' && idx > 0) navigate(-1);
    else navigate(fallback, { replace: true });
  }, [navigate, fallback]);
}

const bundleOf = (html) => (html.match(/\/static\/js\/main\.[0-9a-f]+\.js/) || [])[0] || null;

/**
 * An installed PWA can stay alive for days. When a newer build is live, the
 * next in-app navigation loads it (never mid-typing, never mid-video).
 */
export function useReleaseCheck() {
  const location = useLocation();
  const stale = useRef(false);
  const first = useRef(true);

  useEffect(() => {
    const current = bundleOf(
      Array.from(document.scripts).map((s) => s.getAttribute('src') || '').join(' '));
    if (!current || process.env.NODE_ENV !== 'production') return undefined;
    let last = 0;
    const check = async () => {
      if (document.visibilityState !== 'visible' || Date.now() - last < 5 * 60 * 1000) return;
      last = Date.now();
      try {
        const res = await fetch('/', { cache: 'no-store', credentials: 'omit' });
        const live = bundleOf(await res.text());
        if (live && live !== current) stale.current = true;
      } catch { /* offline: try again later */ }
    };
    document.addEventListener('visibilitychange', check);
    const timer = setInterval(check, 15 * 60 * 1000);
    return () => {
      document.removeEventListener('visibilitychange', check);
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (first.current) { first.current = false; return; }
    if (stale.current) window.location.reload();
  }, [location.pathname]);
}
